import { getDocumentKey, LocalDocument, normalizeFilename } from './documentModel';

type ReconcileInput = {
  base: LocalDocument[]; // application_files + files ya guardados
  locals: LocalDocument[]; // extraFiles locales (pendientes o ya subidos)
  updates?: LocalDocument[]; // opcional: nuevos cambios a aplicar
};

/**
 * Devuelve una lista reconciliada con orden estable y dedupe por clave:
 * - No re-inserta documentos marcados como isDeleted.
 * - Actualiza si la clave coincide; agrega al final si es nuevo.
 * - Mantiene orden previo (propiedad order si existe, si no, posición previa).
 */
export function reconcileDocuments({
  base,
  locals,
  updates = [],
}: ReconcileInput): LocalDocument[] {
  // Normalizar listas copiando para no mutar
  const current = [...locals];

  // Construir mapa de orden previo
  const prevOrder = new Map<string, number>();
  current.forEach((doc, idx) => {
    const key = getDocumentKey(doc);
    if (key && !prevOrder.has(key)) {
      prevOrder.set(key, doc.order ?? idx);
    }
  });

  // Helper de búsqueda por clave/filename normalizado
  const findIndexByKey = (doc: LocalDocument) => {
    const key = getDocumentKey(doc);
    return current.findIndex(
      (d) =>
        getDocumentKey(d) === key ||
        normalizeFilename(d.file_name || '') === normalizeFilename(doc.file_name || '')
    );
  };

  // Aplicar base (fuente backend) sin sobreescribir locales existentes
  base.forEach((doc) => {
    const key = getDocumentKey(doc);
    if (!key) return;
    const existingIndex = findIndexByKey(doc);
    if (existingIndex === -1) {
      current.push(doc);
    }
    // si existe, preferir la versión local (no sobreescribimos)
  });

  // Aplicar updates (cambios recientes) sobre lo existente
  updates.forEach((doc) => {
    const key = getDocumentKey(doc);
    if (!key) return;
    const existingIndex = findIndexByKey(doc);
    if (existingIndex === -1) {
      current.push(doc);
    } else {
      current[existingIndex] = { ...current[existingIndex], ...doc };
    }
  });

  // Remover marcados como borrados
  const filtered = current.filter((doc) => !doc.isDeleted);

  // Reasignar order estable
  const orderCache = new Map<string, number>();
  let counter = prevOrder.size;
  const getOrder = (doc: LocalDocument) => {
    const key = getDocumentKey(doc);
    if (key && prevOrder.has(key)) return prevOrder.get(key)!;
    if (doc.order !== undefined) return doc.order;
    if (!key) return counter++;
    if (!orderCache.has(key)) orderCache.set(key, counter++);
    return orderCache.get(key)!;
  };

  return filtered
    .map((doc) => ({ ...doc, order: getOrder(doc) }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

